import classnames from 'classnames';
import React from 'react';

import {Text} from '@albert-io/atomic';

import {isSupplementAnchor} from './annotationAnchors';
import {useAnnotationModeStore} from './AnnotationModeStore';
import AnnotationNoteEditButton from './AnnotationNoteEditButton';
import {HIGHLIGHT_BACKGROUND_CLASS} from './annotationHighlightStyles';
import {supplementNoteLabel} from './annotationSelectors';
import type {Annotation} from './annotationTypes';

interface AnnotationNoteListProps {
  /** Marks that carry a note, in document order; a text mark is quoted by its anchor text. */
  annotations: Annotation[];
  editingNoteId: string | undefined;
  onEditNote: (annotation: Annotation) => void;
}

/**
 * Each note under the text it was written about. An underlined mark is quoted
 * underlined rather than filled, since it has no fill of its own to restate.
 */
export default function AnnotationNoteList({
  annotations,
  editingNoteId,
  onEditNote
}: AnnotationNoteListProps) {
  const mountedSupplements = useAnnotationModeStore((state) => state.mountedSupplements);

  return (
    <ul className='m-x0 gap-x2 p-x0 flex list-none flex-col'>
      {annotations.map((annotation) => {
        const {id, anchor, note, color, type} = annotation;
        return (
          <li key={id} className='gap-x1 flex flex-col'>
            {/* Beside the quote's first line: this quote is unclipped, and
                centring floats the pencil adrift of a wrapped one. */}
            <div className='gap-x1 flex items-start'>
              {/* A whole-supplement mark quotes no text; its label stands in. */}
              <Text
                size='s'
                className={classnames(
                  // The Figma quotes two to three lines, then truncates.
                  'rounded-x1 px-x1 py-x0-half min-w-0 flex-1 leading-[1.4] wrap-break-word line-clamp-3',
                  {
                    [HIGHLIGHT_BACKGROUND_CLASS[color]]: type === 'highlight',
                    underline: type === 'underline'
                  }
                )}
              >
                {isSupplementAnchor(anchor)
                  ? supplementNoteLabel(mountedSupplements, anchor.regionId)
                  : `“${anchor.exact}”`}
              </Text>
              <AnnotationNoteEditButton
                anchor={anchor}
                isEditing={id === editingNoteId}
                onClick={() => onEditNote(annotation)}
              />
            </div>
            {/* Notes merged from combined marks are newline-separated (annotationMerge). */}
            <Text size='s' className='leading-[1.4] wrap-break-word whitespace-pre-wrap'>
              {note}
            </Text>
          </li>
        );
      })}
    </ul>
  );
}
